import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { DELEGATION_MAX_ROLES, type DelegationMaxRole, type DelegationPropose } from '@detect/shared';
import { useTenant } from '../../contexts/TenantContext';
import { delegationService, type DelegationView } from '../../services/delegations';
import { clientService } from '../../services/clients';
import { usePickerOptions } from '../../hooks/usePickerOptions';
import { LoadingSpinner, ErrorState, EmptyState, Modal } from '../../components/common';
import { formatDateTime } from '../../utils/formatters';
import {
  DEFAULT_TERMS,
  canSelectMore,
  selectionSummary,
  ROLE_LABELS,
  availableActions,
  describeDelegation,
  termsToApi,
  type Terms,
} from './delegationUi';

/**
 * Management access (#16; docs/architecture/delegation.md). A business grants a managing provider a
 * revocable, scoped management grant (or accepts one a provider asked for); a provider proposes or
 * accepts one for its customers. Only the organization being managed ever sets the terms. The API
 * enforces everything; this tab shapes it. Direct account admins only.
 */
export function ManagementTab() {
  const { tenantId } = useTenant();
  const [rows, setRows] = useState<DelegationView[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [proposing, setProposing] = useState<{ counterpart: string; as: 'grantor' | 'grantee' } | null>(null);
  const [accepting, setAccepting] = useState<DelegationView | null>(null);

  const clientsQuery = usePickerOptions(['clients'], (tid, startAfter) =>
    clientService.list(tid, { limit: 100, startAfter })
  );
  const clientNames = new Map((clientsQuery.data?.items ?? []).map((c) => [c.id, c.name]));

  /** Bumped whenever the tenant changes: a response (list or action) that belongs to an older tenant
   *  must never land on the current one, and row ids are only valid for the tenant that listed them. */
  const generation = useRef(0);
  const load = useCallback(() => {
    if (!tenantId) return;
    const gen = generation.current;
    setError(null);
    delegationService.list(tenantId).then(
      (r) => generation.current === gen && setRows(r),
      (e: unknown) => generation.current === gen && setError(e)
    );
  }, [tenantId]);
  useEffect(() => {
    generation.current += 1;
    setRows(null);
    setActionError(null);
    setProposing(null);
    setAccepting(null);
    load();
  }, [load]);

  const act = async (fn: () => Promise<unknown>) => {
    const gen = generation.current;
    setActionError(null);
    try {
      await fn();
      if (generation.current === gen) load();
    } catch (err) {
      if (generation.current === gen) setActionError(err);
    }
  };

  if (error !== null && rows === null) return <ErrorState error={error} onRetry={load} />;
  if (rows === null) return <LoadingSpinner />;

  return (
    <div>
      <p className="text-muted">
        Let a service provider manage this organization, or manage your customers. Access is limited to a
        role and the clients you choose, and can be ended by either side at any time, effective
        immediately. Everything the provider does is recorded in this organization&apos;s audit log with
        their organization named. Account admins always keep their own members, API tokens and this page.
      </p>
      <div className="page-actions tab-actions">
        <button type="button" className="btn btn-primary" onClick={() => setProposing({ counterpart: '', as: 'grantor' })}>
          New management relationship
        </button>
      </div>
      {actionError !== null && <ErrorState error={actionError} />}
      {error !== null && <ErrorState error={error} onRetry={load} />}
      {rows.length === 0 ? (
        <EmptyState
          title="No management relationships"
          description="Propose one to a service provider, or to a customer you manage."
        />
      ) : (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Organization</th>
                <th>Status</th>
                <th>Details</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id}>
                  <td>
                    {d.counterpartName ?? d.counterpartTenantId}
                    <div className="text-muted">
                      {d.side === 'grantor' ? 'manages you' : d.side === 'grantee' ? 'you manage them' : ''}
                    </div>
                  </td>
                  <td>{d.status}</td>
                  <td>
                    {describeDelegation(d, clientNames)}
                    <div className="text-muted">{formatDateTime(d.decidedAt ?? d.proposedAt)}</div>
                  </td>
                  <td className="row-actions">
                    {availableActions(d).map((a) => {
                      if (a === 'accept')
                        return (
                          <button
                            key={a}
                            type="button"
                            className="btn btn-primary"
                            onClick={() => {
                              if (!tenantId) return;
                              // The organization being managed sets the terms; the provider accepts as offered.
                              if (d.side === 'grantor') setAccepting(d);
                              else void act(() => delegationService.accept(tenantId, d.id));
                            }}
                          >
                            Accept
                          </button>
                        );
                      if (a === 'decline')
                        return (
                          <button
                            key={a}
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => tenantId && void act(() => delegationService.decline(tenantId, d.id))}
                          >
                            Decline
                          </button>
                        );
                      if (a === 'revoke')
                        return (
                          <button
                            key={a}
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => {
                              if (!tenantId) return;
                              const msg =
                                d.status === 'active'
                                  ? `End management access with ${d.counterpartName ?? d.counterpartTenantId}? It stops immediately.`
                                  : 'Cancel this request?';
                              if (window.confirm(msg)) void act(() => delegationService.revoke(tenantId, d.id));
                            }}
                          >
                            {d.status === 'active' ? 'End access' : 'Cancel'}
                          </button>
                        );
                      return (
                        <button
                          key={a}
                          type="button"
                          className="btn btn-ghost"
                          onClick={() => setProposing({ counterpart: d.counterpartTenantId, as: d.side ?? 'grantor' })}
                        >
                          Propose again
                        </button>
                      );
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {proposing && (
        <ProposeModal
          initialCounterpart={proposing.counterpart}
          initialAs={proposing.as}
          onClose={() => setProposing(null)}
          onDone={() => {
            setProposing(null);
            load();
          }}
        />
      )}
      {accepting && (
        <AcceptModal
          delegation={accepting}
          onClose={() => setAccepting(null)}
          onDone={() => {
            setAccepting(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function TermsFields({ terms, setTerms }: { terms: Terms; setTerms: (t: Terms) => void }) {
  const clientsQuery = usePickerOptions(['clients'], (tid, startAfter) =>
    clientService.list(tid, { limit: 100, startAfter })
  );
  const clients = clientsQuery.data?.items ?? [];
  const clientsFailed = clientsQuery.error !== null && clientsQuery.error !== undefined;
  return (
    <>
      <div className="form-field">
        <label htmlFor="mgmt-role">Highest role the provider can hold here</label>
        <select
          id="mgmt-role"
          value={terms.maxRole}
          onChange={(e) => setTerms({ ...terms, maxRole: e.target.value as DelegationMaxRole })}
        >
          {DELEGATION_MAX_ROLES.map((r) => (
            <option key={r} value={r}>
              {ROLE_LABELS[r]}
            </option>
          ))}
        </select>
        <p className="text-muted">
          Account admin cannot be granted: you keep your own members, API tokens and this page.
        </p>
      </div>
      <fieldset className="form-field">
        <legend>Which clients</legend>
        <label className="checkbox-label">
          <input
            type="radio"
            name="mgmt-scope"
            checked={terms.allClients}
            onChange={() => setTerms({ ...terms, allClients: true })}
          />
          All clients
        </label>
        <label className="checkbox-label">
          <input
            type="radio"
            name="mgmt-scope"
            checked={!terms.allClients}
            disabled={clientsFailed}
            onChange={() => setTerms({ ...terms, allClients: false })}
          />
          Only these clients
        </label>
        {clientsFailed && <p className="text-muted">The client list could not be loaded, so only &quot;All clients&quot; is available right now.</p>}
        {!terms.allClients && clientsQuery.isLoading && <p className="text-muted">Loading clients…</p>}
        {!terms.allClients && clientsQuery.data?.truncated && (
          <p className="text-muted">Only the first clients are listed here; later ones cannot be chosen from this list.</p>
        )}
        {!terms.allClients && <p className="text-muted">{selectionSummary(terms.clientIds.length)}</p>}
        {!terms.allClients &&
          clients.map((c) => (
            <label key={c.id} className="checkbox-label checkbox-inline">
              <input
                type="checkbox"
                checked={terms.clientIds.includes(c.id)}
                disabled={!terms.clientIds.includes(c.id) && !canSelectMore(terms.clientIds.length)}
                onChange={(e) =>
                  setTerms({
                    ...terms,
                    clientIds: e.target.checked
                      ? [...terms.clientIds, c.id]
                      : terms.clientIds.filter((x) => x !== c.id),
                  })
                }
              />
              {c.name}
            </label>
          ))}
      </fieldset>
    </>
  );
}

function ProposeModal({
  initialCounterpart,
  initialAs,
  onClose,
  onDone,
}: {
  initialCounterpart: string;
  /** Re-proposing keeps the side this organization was on. */
  initialAs: 'grantor' | 'grantee';
  onClose: () => void;
  onDone: () => void;
}) {
  const { tenantId } = useTenant();
  const [counterpart, setCounterpart] = useState(initialCounterpart);
  const [as, setAs] = useState<'grantor' | 'grantee'>(initialAs);
  const [terms, setTerms] = useState<Terms>(DEFAULT_TERMS);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;
    setBusy(true);
    setError(null);
    try {
      const input: DelegationPropose =
        as === 'grantor'
          ? { counterpartTenantId: counterpart.trim(), as, ...termsToApi(terms) }
          : { counterpartTenantId: counterpart.trim(), as };
      await delegationService.propose(tenantId, input);
      onDone();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New management relationship" onClose={onClose} wide>
      <form onSubmit={submit}>
        {error !== null && <ErrorState error={error} />}
        <fieldset className="form-field">
          <legend>This organization is</legend>
          <label className="checkbox-label">
            <input type="radio" name="mgmt-as" checked={as === 'grantor'} onChange={() => setAs('grantor')} />
            the one being managed (I am inviting a provider)
          </label>
          <label className="checkbox-label">
            <input type="radio" name="mgmt-as" checked={as === 'grantee'} onChange={() => setAs('grantee')} />
            the provider (I am asking to manage a customer)
          </label>
        </fieldset>
        <div className="form-field">
          <label htmlFor="mgmt-counterpart">
            {as === 'grantor' ? "Provider's organization id" : "Customer's organization id"}
          </label>
          <input
            id="mgmt-counterpart"
            required
            value={counterpart}
            onChange={(e) => setCounterpart(e.target.value)}
          />
          <p className="text-muted">
            They see and answer the request in their own Settings. We do not say whether an id exists.
          </p>
        </div>
        {as === 'grantor' ? (
          <TermsFields terms={terms} setTerms={setTerms} />
        ) : (
          <p className="text-muted">The customer chooses the role and the clients when they accept.</p>
        )}
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? 'Sending…' : 'Send request'}
        </button>
      </form>
    </Modal>
  );
}

function AcceptModal({
  delegation,
  onClose,
  onDone,
}: {
  delegation: DelegationView;
  onClose: () => void;
  onDone: () => void;
}) {
  const { tenantId } = useTenant();
  const [terms, setTerms] = useState<Terms>(DEFAULT_TERMS);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;
    setBusy(true);
    setError(null);
    try {
      await delegationService.accept(tenantId, delegation.id, termsToApi(terms));
      onDone();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Allow ${delegation.counterpartName ?? delegation.counterpartTenantId} to manage this organization`} onClose={onClose} wide>
      <form onSubmit={submit}>
        {error !== null && <ErrorState error={error} />}
        <TermsFields terms={terms} setTerms={setTerms} />
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? 'Saving…' : 'Allow access'}
        </button>
      </form>
    </Modal>
  );
}
